/**
 * Punctuation-affix extraction — shared safe helper used by both the hybrid engine
 * (`hybridApply/explodePairs`) and the legacy AI-OOXML redline generator
 * (`RobustXmlTrackChangesGenerator.computeHybridDiff`) so the two renderers agree.
 *
 * Given a delete/insert replacement pair, it computes the longest common char-level prefix and
 * suffix that both sides share. Those affixes are candidates to become "kept" (unchanged) instead
 * of being struck-and-re-inserted. This is what turns
 *   delete `המלווה".` + insert `המלווה" [ראה סעיף 4.1 …]`
 * into
 *   keep `המלווה"` + delete `.` + insert ` [ראה סעיף 4.1 …]`,
 * so a purely punctuation change at a token edge does not needlessly redline the shared word.
 *
 * ## Safety gate — "never partially strike a real word"
 * We only extract when the DELETE remainder in the middle contains NO Unicode letter or digit.
 * That guarantees no fragment of a struck word is being kept: everything shared is genuinely
 * unchanged content. This is what preserves the `forth`→`for` / `recieve`→`receive` behavior — the
 * old word stays a whole-word delete because its remainder still holds letters.
 *
 * Pure. Unit-tested via the consumer suites (`explodePairs.test.ts`, generator tests).
 */

/** True iff `s` contains any Unicode letter or digit — i.e. any "word" character. */
export const containsWordChar = (s: string): boolean => /[\p{L}\p{N}]/u.test(s);

export interface PunctuationAffixes {
  /** Chars shared at the start of both delete and insert (candidates for a `keep`). */
  prefix: string;
  /** Chars shared at the end of both delete and insert (candidates for a `keep`). */
  suffix: string;
  /** The delete value with prefix/suffix stripped — what actually needs to be struck. */
  deleteMiddle: string;
  /** The insert value with prefix/suffix stripped — what actually needs to be inserted. */
  insertMiddle: string;
}

/**
 * Compute the char-level common prefix/suffix of a delete/insert pair and return them along with
 * the middle remainders. Returns `null` when the extraction is unsafe (the delete middle still
 * contains a letter/digit — extracting would partially strike a real word) or when there is no
 * common affix at all.
 */
export function computePunctuationAffixes(
  deleteVal: string,
  insertVal: string,
): PunctuationAffixes | null {
  const minLen = Math.min(deleteVal.length, insertVal.length);
  let p = 0;
  while (p < minLen && deleteVal[p] === insertVal[p]) p += 1;
  let s = 0;
  while (
    s < minLen - p &&
    deleteVal[deleteVal.length - 1 - s] === insertVal[insertVal.length - 1 - s]
  ) {
    s += 1;
  }
  if (p === 0 && s === 0) return null;

  const deleteMiddle = deleteVal.slice(p, deleteVal.length - s);
  const insertMiddle = insertVal.slice(p, insertVal.length - s);
  if (containsWordChar(deleteMiddle)) return null;

  return {
    prefix: deleteVal.slice(0, p),
    suffix: deleteVal.slice(deleteVal.length - s),
    deleteMiddle,
    insertMiddle,
  };
}
